import { useState, useEffect, useMemo, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import type { ConferenceOutputBilling } from "@/services/conferenceOutputPublicService";
import {
    GraduationCap, Loader2, Search, ChevronLeft, ChevronRight, Mail, Send, Upload,
    FileUp, Trash2, Calendar, CalendarClock, Download, FileText, Clock, CheckCircle2, ArrowRightCircle, Edit2,
    Check, ChevronsUpDown, ChevronDown, ChevronUp, Eye, BookOpen, Users, StickyNote, History,
    AlertTriangle, XCircle, ListChecks as ListChecksIcon, FileCheck2, Wallet, MailCheck, Filter,
    X, RotateCcw, CreditCard, BarChart3,
} from "lucide-react";
import OutputDecisionsRecapTab from "./OutputDecisionsRecapTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
    useAdminOutputDecisions,
    useAdminOutputDecision,
    useSendOutputDecisionEmail,
    useSendOutputBulkEmail,
    useExtendSubmissionDeadline,
    useBulkExtendOutputDecisionDeadline,
    useUpdateOutputDecision,
    useImportJournalMapping,
    useImportProceedingMapping,
    useImportConsolidatedMapping,
    useAdminMasterScopus,
    useAdminMasterSinta,
    useAdminImportBatches,
    downloadOutputTemplate,
    exportOutputDecisionsExcel,
    useCancelOutputDecision,
    useGenerateOutputDecisionInvoice,
    useTestSendOutputDecisionEmail,
    type DecisionFilters,
    type CancelDecisionSummary,
} from "@/services/adminConferenceOutputService";

const IMPORT_TYPE_LABELS: Record<string, string> = {
    master_journals: "Master Jurnal",
    journal_mapping: "Pemetaan Jurnal",
    proceeding_mapping: "Pemetaan Proceeding",
    consolidated_mapping: "Pemetaan Konsolidasi",
};

const BILLING_FILTER_LABELS: Record<string, string> = {
    ready_to_bill: "Siap Buat Tagihan (Berkas Lengkap)",
    submitted_unpaid: "Berkas Lengkap Belum Lunas",
    paid: "Sudah Dibayar (Lunas)",
    unpaid: "Belum Dibayar (Unpaid)",
    has_billing: "Sudah Ada Tagihan",
    no_billing: "Belum Ada Tagihan",
    expired: "Tagihan Kadaluarsa",
    cancelled: "Tagihan Dibatalkan",
};

const REGISTRATION_STATUS_LABELS: Record<string, string> = {
    ready_to_bill: "Siap Buat Tagihan (Belum Ada Tagihan)",
    submitted: "Sudah Unggah Lengkap (Submitted)",
    draft: "Masih Mengisi (Draft)",
    not_started: "Belum Mengisi Form",
};

const formatDate = (value: string | null | undefined) => {
    if (!value) return "-";
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    // App backend berjalan di timezone Asia/Makassar (WITA) - kolom 'date' (mis. decision_deadline)
    // disimpan sebagai tengah malam WITA lalu diserialize sebagai UTC. Kalau sekadar slice(0,10)
    // dari string ISO mentah, hasilnya bisa mundur 1 hari (mis. WITA 10 Okt 00:00 = UTC 9 Okt 16:00).
    // Format eksplisit dengan timeZone Asia/Makassar supaya tanggal yang tampil selalu konsisten
    // dengan tanggal bisnis yang dimaksud, terlepas dari timezone browser admin yang membuka halaman ini.
    return new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Makassar" }).format(date);
};

// Konversi ISO datetime -> "YYYY-MM-DD" di timezone Asia/Makassar, untuk prefill <input type="date">.
// Locale 'en-CA' kebetulan berformat YYYY-MM-DD persis format yang dibutuhkan native date input.
const toDateInputValue = (value: string | null | undefined) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Makassar", year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
};

export default function OutputDecisionsPage() {
    const [filters, setFilters] = useState<DecisionFilters>({ page: 1, per_page: 20 });
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState<string>("decisions");

    useEffect(() => {
        const t = setTimeout(() => {
            setFilters((f) => ({ ...f, search: search || undefined, page: 1 }));
        }, 500);
        return () => clearTimeout(t);
    }, [search]);

    const { data: conferences } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/conference-billing/accessible-conferences");
            return data.data;
        },
    });

    const { data, isLoading } = useAdminOutputDecisions(filters);

    // Kartu total - reuse endpoint index yang sama dengan per_page:1, cukup baca field `total`
    // dari respons paginasi tanpa perlu bikin endpoint stats terpisah di backend.
    const statFilters = { conference_id: filters.conference_id, output_route: filters.output_route };
    const { data: totalAll } = useAdminOutputDecisions({ ...statFilters, per_page: 1, page: 1 });
    const { data: totalPending } = useAdminOutputDecisions({ ...statFilters, author_decision: "pending", per_page: 1, page: 1 });
    const { data: totalAgreed } = useAdminOutputDecisions({ ...statFilters, author_decision: "agreed", per_page: 1, page: 1 });
    const { data: totalDeclined } = useAdminOutputDecisions({ ...statFilters, author_decision: "declined", per_page: 1, page: 1 });

    const sendEmail = useSendOutputDecisionEmail();
    const sendBulkEmail = useSendOutputBulkEmail();
    const extendDeadline = useExtendSubmissionDeadline();
    const bulkExtend = useBulkExtendOutputDecisionDeadline();
    const updateDecision = useUpdateOutputDecision();
    const cancelDecision = useCancelOutputDecision();

    // --- Batalkan Keputusan - kalau sudah ada file/tagihan, backend menolak dengan
    // confirm_required berisi summary, baru kita tampilkan dialog konfirmasi ini.
    const [cancelConfirm, setCancelConfirm] = useState<{ id: number; summary: CancelDecisionSummary } | null>(null);

    const handleCancelDecision = (id: number, force = false) => {
        cancelDecision.mutate(
            { id, force },
            {
                onSuccess: (res) => {
                    toast.success(res.message || "Keputusan berhasil dibatalkan");
                    setCancelConfirm(null);
                    setDetailId(null);
                },
                onError: (err: any) => {
                    const data = err.response?.data;
                    if (err.response?.status === 409 && data?.status === "confirm_required" && data?.summary) {
                        setCancelConfirm({ id, summary: data.summary });
                    } else {
                        toast.error(data?.message || "Gagal membatalkan keputusan");
                    }
                },
            }
        );
    };

    // --- Generate Ulang / Terbitkan Tagihan Prosiding
    const generateInvoice = useGenerateOutputDecisionInvoice();
    const [generateInvoiceConfirm, setGenerateInvoiceConfirm] = useState<{ id: number; title: string; author: string; publisher: string } | null>(null);
    const [sendEmailWithInvoice, setSendEmailWithInvoice] = useState(true);

    const handleGenerateInvoice = (id: number) => {
        generateInvoice.mutate(
            { id, send_email: sendEmailWithInvoice },
            {
                onSuccess: (res: any) => {
                    toast.success(res.message || "Tagihan pembayaran berhasil diterbitkan");
                    setGenerateInvoiceConfirm(null);
                },
                onError: (err: any) => {
                    toast.error(err.response?.data?.message || "Gagal menerbitkan tagihan pembayaran");
                },
            }
        );
    };

    const { data: scopusJournals } = useAdminMasterScopus();
    const { data: sintaJournals } = useAdminMasterSinta();

    const [isImportOpen, setIsImportOpen] = useState(false);
    const [importConferenceId, setImportConferenceId] = useState<string>("");
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importResult, setImportResult] = useState<any>(null);

    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);
    const [expandedBatchId, setExpandedBatchId] = useState<number | null>(null);
    const { data: historyData, isLoading: isLoadingHistory } = useAdminImportBatches({ page: historyPage, per_page: 10 });

    const [isBulkOpen, setIsBulkOpen] = useState(false);
    const [bulkForce, setBulkForce] = useState(false);
    const [bulkResult, setBulkResult] = useState<any>(null);
    const [bulkEmailTemplateType, setBulkEmailTemplateType] = useState<"regular" | "reminder">("regular");

    // State perpanjang deadline massal
    const [isBulkExtendOpen, setIsBulkExtendOpen] = useState(false);
    const [bulkExtendDate, setBulkExtendDate] = useState("2026-10-17");
    const [bulkExtendConferenceId, setBulkExtendConferenceId] = useState<string>("all");
    const [bulkExtendRoute, setBulkExtendRoute] = useState<"all" | "journal" | "proceeding">("all");
    const [bulkExtendAuthorDecision, setBulkExtendAuthorDecision] = useState<"all" | "pending" | "agreed" | "declined">("all");
    const [bulkExtendBillingStatus, setBulkExtendBillingStatus] = useState<string>("all");
    const [bulkExtendSendEmail, setBulkExtendSendEmail] = useState(true);
    const [bulkExtendTemplateType, setBulkExtendTemplateType] = useState<"reminder" | "regular">("reminder");

    // Hitung total paper yang terdampak perpanjang deadline massal secara reaktif sesuai filter aktif
    const bulkExtendFilters: DecisionFilters = useMemo(() => ({
        conference_id: bulkExtendConferenceId !== "all" ? bulkExtendConferenceId : undefined,
        output_route: bulkExtendRoute !== "all" ? (bulkExtendRoute as "journal" | "proceeding") : undefined,
        author_decision: bulkExtendAuthorDecision !== "all" ? (bulkExtendAuthorDecision as "pending" | "agreed" | "declined") : undefined,
        billing_status: bulkExtendBillingStatus !== "all" ? (bulkExtendBillingStatus as any) : undefined,
        per_page: 1,
        page: 1,
    }), [bulkExtendConferenceId, bulkExtendRoute, bulkExtendAuthorDecision, bulkExtendBillingStatus]);

    const { data: bulkExtendCountData, isLoading: isLoadingBulkExtendCount, isFetching: isFetchingBulkExtendCount } = useAdminOutputDecisions(
        bulkExtendFilters,
        { enabled: isBulkExtendOpen }
    );
    const bulkExtendTotalAffected = bulkExtendCountData?.total ?? 0;

    // State test email (khusus uji coba ke pengembang/admin)
    const [isTestEmailOpen, setIsTestEmailOpen] = useState(false);
    const [testTargetEmail, setTestTargetEmail] = useState("satria.widnyana@undiksha.ac.id");
    const [testTemplateType, setTestTemplateType] = useState<"reminder" | "regular">("reminder");
    const testSendEmail = useTestSendOutputDecisionEmail();

    const [extendTarget, setExtendTarget] = useState<any>(null);
    const [extendDate, setExtendDate] = useState("");
    const [extendReason, setExtendReason] = useState("");

    const [editTarget, setEditTarget] = useState<any>(null);
    const [editForm, setEditForm] = useState<{ scopus_journal_id: string; sinta_journal_id: string; journal_admin_note: string; decision_deadline: string }>({
        scopus_journal_id: "", sinta_journal_id: "", journal_admin_note: "", decision_deadline: "",
    });
    const [openScopusCombobox, setOpenScopusCombobox] = useState(false);
    const [openSintaCombobox, setOpenSintaCombobox] = useState(false);

    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const toggleExpandRow = (id: number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const [detailId, setDetailId] = useState<number | null>(null);
    const { data: detailData, isLoading: isLoadingDetail } = useAdminOutputDecision(detailId ?? undefined);

    // --- Export Dialog - mengikuti filter yang sedang aktif di halaman ---
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async () => {
        setIsExporting(true);
        try {
            await exportOutputDecisionsExcel(filters);
            toast.success("File Excel berhasil diunduh");
            setIsExportOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Gagal mengunduh file export");
        } finally {
            setIsExporting(false);
        }
    };

    const findJournalName = (d: any) => {
        if (d.scopus_journal_id) return d.scopus_journal?.journal_name ? `${d.scopus_journal.journal_name} (${d.scopus_journal.quartile})` : scopusJournals?.find((j) => j.id === d.scopus_journal_id)?.journal_name;
        if (d.sinta_journal_id) return d.sinta_journal?.journal_name ? `${d.sinta_journal.journal_name} (${d.sinta_journal.sinta_rank})` : sintaJournals?.find((j) => j.id === d.sinta_journal_id)?.journal_name;
        return null;
    };

    const openEdit = (d: any) => {
        setEditTarget(d);
        setEditForm({
            scopus_journal_id: d.scopus_journal_id ? String(d.scopus_journal_id) : "",
            sinta_journal_id: d.sinta_journal_id ? String(d.sinta_journal_id) : "",
            journal_admin_note: d.journal_admin_note || "",
            decision_deadline: toDateInputValue(d.decision_deadline),
        });
    };

    const handleSaveEdit = async () => {
        if (!editTarget) return;
        try {
            await updateDecision.mutateAsync({
                id: editTarget.id,
                payload: {
                    scopus_journal_id: editForm.scopus_journal_id ? parseInt(editForm.scopus_journal_id) : null,
                    sinta_journal_id: editForm.sinta_journal_id ? parseInt(editForm.sinta_journal_id) : null,
                    journal_admin_note: editForm.journal_admin_note || null,
                    decision_deadline: editForm.decision_deadline || null,
                },
            });
            toast.success("Data keputusan berhasil diperbarui");
            setEditTarget(null);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal memperbarui data");
        }
    };

    const importJournalMapping = useImportJournalMapping();
    const importProceedingMapping = useImportProceedingMapping();
    const importConsolidatedMapping = useImportConsolidatedMapping();

    const decisions = data?.data ?? [];
    const filteredDecisions = decisions;

    const handleSendEmail = (id: number, templateType: "regular" | "reminder" = "regular") => {
        sendEmail.mutate(
            { id, email_template_type: templateType },
            {
                onSuccess: (res: any) => toast.success(res.message || "Email terkirim"),
                onError: (err: any) => toast.error(err.response?.data?.message || "Gagal mengirim email"),
            }
        );
    };

    const handleBulkSend = (mode: "journal" | "proceeding" | undefined) => {
        sendBulkEmail.mutate(
            {
                conference_id: filters.conference_id,
                output_route: mode,
                billing_status: filters.billing_status,
                email_template_type: bulkEmailTemplateType,
                force: bulkForce,
                limit: 50,
            },
            {
                onSuccess: (res) => {
                    setBulkResult(res);
                    toast.success(res.message);
                },
                onError: (err: any) => toast.error(err.response?.data?.message || "Gagal mengirim email massal"),
            }
        );
    };

    const handleImportSubmit = (kind: "journal" | "proceeding" | "consolidated") => {
        if (!importFile) {
            toast.error("Pilih file Excel terlebih dahulu");
            return;
        }
        const formData = new FormData();
        formData.append("file", importFile);

        const onSuccess = (data: any) => {
            setImportResult(data);
            toast.success(data.message || "Import berhasil");
        };
        const onError = (error: any) => {
            const message = error.response?.data?.message || "Gagal import data";
            toast.error(typeof message === "string" ? message : JSON.stringify(message));
        };

        if (kind === "consolidated") {
            importConsolidatedMapping.mutate(formData, { onSuccess, onError });
        } else if (kind === "journal") {
            if (!importConferenceId) { toast.error("Pilih track konferensi terlebih dahulu"); return; }
            importJournalMapping.mutate({ conference_id: importConferenceId, formData }, { onSuccess, onError });
        } else {
            if (!importConferenceId) { toast.error("Pilih track konferensi terlebih dahulu"); return; }
            importProceedingMapping.mutate({ conference_id: importConferenceId, formData }, { onSuccess, onError });
        }
    };

    const handleExtend = async () => {
        if (!extendTarget || !extendDate) return;
        try {
            await extendDeadline.mutateAsync({ id: extendTarget.id, new_deadline: extendDate, reason: extendReason });
            toast.success("Deadline berhasil diperpanjang");
            setExtendTarget(null);
            setExtendDate("");
            setExtendReason("");
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal memperpanjang deadline");
        }
    };

    const handleExecuteBulkExtend = async () => {
        if (!bulkExtendDate) {
            toast.error("Pilih tanggal deadline baru terlebih dahulu");
            return;
        }
        try {
            const res = await bulkExtend.mutateAsync({
                conference_id: bulkExtendConferenceId === "all" ? undefined : bulkExtendConferenceId,
                output_route: bulkExtendRoute,
                author_decision: bulkExtendAuthorDecision,
                billing_status: bulkExtendBillingStatus === "all" ? undefined : bulkExtendBillingStatus,
                new_deadline: bulkExtendDate,
                send_email: bulkExtendSendEmail,
                email_template_type: bulkExtendTemplateType,
            });
            toast.success(res.message || "Berhasil memperpanjang deadline massal");
            setIsBulkExtendOpen(false);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal memperpanjang deadline massal");
        }
    };

    const handleSendTestEmail = async () => {
        if (!testTargetEmail) {
            toast.error("Masukkan alamat email tujuan test");
            return;
        }
        try {
            const res = await testSendEmail.mutateAsync({
                target_email: testTargetEmail,
                email_template_type: testTemplateType,
            });
            toast.success(res.message || "Test email berhasil dikirim!");
            setIsTestEmailOpen(false);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal mengirim test email");
        }
    };

    const routeBadge = (route: string, publisher: string | null) => {
        if (route === "journal") return <span className="text-[11px] font-bold uppercase px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded-md">Jurnal</span>;
        return <span className="text-[11px] font-bold uppercase px-2.5 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 rounded-md">Proceeding {publisher ? `(${publisher})` : ""}</span>;
    };

    const decisionBadge = (status: string) => {
        const map: Record<string, string> = {
            pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
            agreed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400",
            declined: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
        };
        return <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-md ${map[status] || ""}`}>{status}</span>;
    };

    const formatCurrency = (amount: string | number, currency: string) =>
        new Intl.NumberFormat(currency === "USD" ? "en-US" : "id-ID", { style: "currency", currency, minimumFractionDigits: 0 }).format(Number(amount));

    const billingBadge = (billing: ConferenceOutputBilling | null | undefined) => {
        if (!billing) {
            return <span className="text-[11px] font-semibold text-slate-400 italic">Belum ada tagihan</span>;
        }
        const map: Record<string, string> = {
            unpaid: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
            paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400",
            expired: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
            cancelled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
        };
        return (
            <div className="flex items-center gap-1.5">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md whitespace-nowrap ${map[billing.status] || ""}`}>{billing.status}</span>
                <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">{formatCurrency(billing.amount, billing.currency)}</span>
            </div>
        );
    };

    // Kelengkapan file + status tagihan - cuma relevan untuk jalur proceeding, karena jurnal
    // tidak punya tahapan camera-ready/copyright/tagihan di sistem ini.
    const completenessCell = (d: any) => {
        if (d.output_route !== "proceeding") return <span className="text-xs text-slate-400">-</span>;
        const reg = d.output_registration;
        return (
            <div className="space-y-1.5">
                <div className="flex items-center gap-3">
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                {reg?.camera_ready_file_path ? (
                                    <a href={reg.camera_ready_file_path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline">
                                        <FileCheck2 className="w-3.5 h-3.5" /> <span className="text-[11px] font-semibold">Camera-ready</span>
                                    </a>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-slate-400">
                                        <XCircle className="w-3.5 h-3.5" /> <span className="text-[11px] font-semibold">Camera-ready</span>
                                    </span>
                                )}
                            </TooltipTrigger>
                            <TooltipContent><p>{reg?.camera_ready_file_path ? "Klik untuk unduh file" : "Belum diunggah penulis"}</p></TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <div className="flex items-center gap-3">
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                {reg?.copyright_form_file_path ? (
                                    <a href={reg.copyright_form_file_path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline">
                                        <FileCheck2 className="w-3.5 h-3.5" /> <span className="text-[11px] font-semibold">Copyright Form</span>
                                    </a>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-slate-400">
                                        <XCircle className="w-3.5 h-3.5" /> <span className="text-[11px] font-semibold">Copyright Form</span>
                                    </span>
                                )}
                            </TooltipTrigger>
                            <TooltipContent><p>{reg?.copyright_form_file_path ? "Klik untuk unduh file" : "Belum diunggah penulis"}</p></TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <div className="flex items-center gap-1.5 pt-0.5">
                    <Wallet className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    {billingBadge(reg?.billing)}
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col gap-3 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <GraduationCap className="w-64 h-64 text-blue-600" />
                </div>
                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 flex-shrink-0 border border-blue-100 dark:border-blue-800/50 shadow-sm">
                        <GraduationCap className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Output Conference - Keputusan Publikasi</h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-none">
                            Monitoring keputusan penulis (jurnal vs proceeding), impor pemetaan, dan pengiriman email notifikasi.
                        </p>
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 relative z-10 pt-5 border-t border-slate-100 dark:border-slate-800">
                    {/* Grup Kiri: Alat Data & Riwayat */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            onClick={() => { setIsHistoryOpen(true); setHistoryPage(1); }}
                            variant="outline"
                            className="h-10 px-3.5 sm:px-4 rounded-xl border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800 font-medium text-slate-700 dark:text-slate-300 shadow-sm text-sm"
                        >
                            <History className="w-4 h-4 mr-2 text-slate-500" /> Riwayat Import
                        </Button>
                        <Button
                            onClick={() => setIsExportOpen(true)}
                            variant="outline"
                            className="h-10 px-3.5 sm:px-4 rounded-xl border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800 font-medium text-slate-700 dark:text-slate-300 shadow-sm text-sm"
                        >
                            <Download className="w-4 h-4 mr-2 text-slate-500" /> Export Excel
                        </Button>
                    </div>

                    {/* Grup Kanan: Aksi Operasional Utama */}
                    <div className="flex items-center gap-2.5 flex-wrap justify-start lg:justify-end">
                        {/* Dropdown Aksi Email (Kirim Email Massal & Test Email) */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="h-10 px-3.5 sm:px-4 rounded-xl border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800 font-semibold text-slate-700 dark:text-slate-200 shadow-sm text-sm"
                                >
                                    <Mail className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                                    <span>Aksi Email</span>
                                    <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64 p-1.5 rounded-2xl shadow-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 z-50">
                                <DropdownMenuItem
                                    onClick={() => { setIsBulkOpen(true); setBulkResult(null); }}
                                    className="rounded-xl py-2 px-3 text-sm cursor-pointer flex items-center gap-2.5 font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
                                >
                                    <Send className="w-4 h-4 text-blue-600 shrink-0" />
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-slate-800 dark:text-slate-200">Kirim Email Massal</span>
                                        <span className="text-[11px] text-slate-400">Kirim antrean ke penulis</span>
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => setIsTestEmailOpen(true)}
                                    className="rounded-xl py-2 px-3 text-sm cursor-pointer flex items-center gap-2.5 font-medium text-indigo-700 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                                >
                                    <MailCheck className="w-4 h-4 text-indigo-600 shrink-0" />
                                    <div className="flex flex-col">
                                        <span className="font-semibold">Test Kirim Email Sample</span>
                                        <span className="text-[11px] text-slate-400">Sampel khusus akun admin</span>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Tombol Perpanjang Deadline */}
                        <Button
                            onClick={() => {
                                setBulkExtendConferenceId(filters.conference_id ? String(filters.conference_id) : "all");
                                setBulkExtendRoute(filters.output_route || "all");
                                setBulkExtendAuthorDecision(filters.author_decision || "all");
                                setBulkExtendBillingStatus(filters.billing_status || "all");
                                setIsBulkExtendOpen(true);
                            }}
                            className="h-10 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-sm text-sm"
                        >
                            <CalendarClock className="w-4 h-4 mr-2" />
                            <span>Perpanjang Deadline</span>
                            <span className="hidden xl:inline">&nbsp;Massal</span>
                        </Button>

                        {/* Tombol Import Pemetaan */}
                        <Button
                            onClick={() => { setIsImportOpen(true); setImportResult(null); setImportFile(null); }}
                            className="h-10 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold shadow-md shadow-blue-600/20 text-sm"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            <span>Import Pemetaan</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Navigasi Tab Utama (Rekap Data vs Data Keputusan) */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <TabsList className="bg-slate-100 dark:bg-slate-800/80 p-1.5 rounded-2xl h-13 border border-slate-200 dark:border-slate-700 shadow-2xs">
                        <TabsTrigger
                            value="decisions"
                            className="h-10 px-5 rounded-xl font-bold text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-purple-600 dark:data-[state=active]:text-purple-400 data-[state=active]:shadow-sm flex items-center gap-2 transition-all cursor-pointer"
                        >
                            <FileText className="w-4 h-4 text-purple-500" />
                            <span>Daftar Data & Keputusan Paper</span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold">
                                {data?.total ?? totalAll?.total ?? 0}
                            </span>
                        </TabsTrigger>
                        <TabsTrigger
                            value="rekap"
                            className="h-10 px-5 rounded-xl font-bold text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 data-[state=active]:shadow-sm flex items-center gap-2 transition-all cursor-pointer"
                        >
                            <BarChart3 className="w-4 h-4 text-blue-500" />
                            <span>Tab Rekap Data</span>
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="rekap" className="m-0 focus-visible:outline-none">
                    <OutputDecisionsRecapTab
                        conferences={conferences}
                        onDrillDown={(newFilters) => {
                            setFilters((prev) => ({
                                ...prev,
                                ...newFilters,
                                page: 1,
                            }));
                            setActiveTab("decisions");
                        }}
                    />
                </TabsContent>

                <TabsContent value="decisions" className="m-0 space-y-8 focus-visible:outline-none">
                    {/* Kartu Total */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard icon={FileText} label="Total Paper" value={totalAll?.total} color="blue" />
                        <StatCard icon={Clock} label="Menunggu Keputusan" value={totalPending?.total} color="amber" />
                        <StatCard icon={CheckCircle2} label="Setuju ke Jurnal" value={totalAgreed?.total} color="emerald" />
                        <StatCard icon={ArrowRightCircle} label="Pilih Proceeding" value={totalDeclined?.total} color="purple" />
                    </div>

                    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800 space-y-3.5">
                            {/* Baris 1: Pencarian, Total Info, & Tombol Reset */}
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                                <div className="relative flex-1">
                                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                        <Search className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <Input
                                        type="text"
                                        placeholder="Cari judul paper atau email penulis..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-10 pr-9 h-10 w-full text-sm bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl shadow-2xs focus-visible:ring-1 focus-visible:ring-blue-500"
                                    />
                                    {search && (
                                        <button
                                            onClick={() => setSearch("")}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                                    {(!!filters.conference_id || !!filters.output_route || !!filters.author_decision || !!filters.email_status || !!filters.registration_status || !!filters.billing_status || !!search) && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setSearch("");
                                                setFilters({ page: 1, per_page: filters.per_page });
                                            }}
                                            className="h-10 px-3.5 rounded-xl border-rose-200 dark:border-rose-900/50 bg-rose-50/70 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 hover:bg-rose-100 hover:text-rose-700 dark:hover:bg-rose-950/40 text-xs font-semibold transition-colors shadow-2xs"
                                        >
                                            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                            Reset Filter ({[filters.conference_id, filters.output_route, filters.author_decision, filters.email_status, filters.registration_status, filters.billing_status, search].filter(Boolean).length})
                                        </Button>
                                    )}
                                    <div className="h-10 px-3.5 flex items-center gap-1.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-600 dark:text-slate-300 shadow-2xs">
                                        <span className="text-slate-400">Total:</span>
                                        <span className="font-bold text-slate-900 dark:text-white">{data?.total ?? 0}</span>
                                        <span className="text-slate-400">Paper</span>
                                    </div>
                                </div>
                            </div>

                            {/* Baris 2: Grid 6 Filter Sejajar Proporsional dengan Label & Ikon */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 pt-1">
                                {/* 1. Track */}
                                <div className="w-full space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                                        <GraduationCap className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                        <span>Track</span>
                                    </label>
                                    <Select value={String(filters.conference_id ?? "all")} onValueChange={(v) => setFilters((f) => ({ ...f, conference_id: v === "all" ? undefined : v, page: 1 }))}>
                                        <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                            <SelectValue placeholder="Semua Track" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl max-h-[300px]">
                                            <SelectItem value="all">Semua Track</SelectItem>
                                            {conferences?.map((conf: any) => (
                                                <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>{conf.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* 2. Jalur */}
                                <div className="w-full space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                                        <BookOpen className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                                        <span>Jalur</span>
                                    </label>
                                    <Select value={filters.output_route ?? "all"} onValueChange={(v) => setFilters((f) => ({ ...f, output_route: v === "all" ? undefined : (v as any), page: 1 }))}>
                                        <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                            <SelectValue placeholder="Semua Jalur" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl">
                                            <SelectItem value="all">Semua Jalur</SelectItem>
                                            <SelectItem value="journal">Jurnal</SelectItem>
                                            <SelectItem value="proceeding">Proceeding</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* 3. Keputusan Penulis */}
                                <div className="w-full space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                                        <Users className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                        <span>Keputusan</span>
                                    </label>
                                    <Select value={filters.author_decision ?? "all"} onValueChange={(v) => setFilters((f) => ({ ...f, author_decision: v === "all" ? undefined : (v as any), page: 1 }))}>
                                        <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                            <SelectValue placeholder="Semua Keputusan" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl">
                                            <SelectItem value="all">Semua Keputusan</SelectItem>
                                            <SelectItem value="pending">Pending</SelectItem>
                                            <SelectItem value="agreed">Agreed</SelectItem>
                                            <SelectItem value="declined">Declined</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* 4. Status Email */}
                                <div className="w-full space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                                        <Mail className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                        <span>Status Email</span>
                                    </label>
                                    <Select value={filters.email_status ?? "all"} onValueChange={(v) => setFilters((f) => ({ ...f, email_status: v === "all" ? undefined : (v as any), page: 1 }))}>
                                        <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                            <SelectValue placeholder="Semua Status Email" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl">
                                            <SelectItem value="all">Semua Status Email</SelectItem>
                                            <SelectItem value="sent">Terkirim</SelectItem>
                                            <SelectItem value="unsent">Belum Terkirim</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* 5. Kelengkapan Prosiding */}
                                <div className="w-full space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                                        <FileCheck2 className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                                        <span>Kelengkapan Prosiding</span>
                                    </label>
                                    <Select
                                        value={filters.registration_status ?? "all"}
                                        onValueChange={(v) => setFilters((f) => ({ ...f, registration_status: v === "all" ? undefined : (v as any), page: 1 }))}
                                    >
                                        <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                            <SelectValue placeholder="Semua Kelengkapan" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl shadow-xl max-h-80">
                                            <SelectItem value="all">Semua Kelengkapan</SelectItem>
                                            <SelectItem value="ready_to_bill" className="font-semibold text-purple-600 dark:text-purple-400">
                                                ⚡ Siap Buat Tagihan (Berkas Lengkap &amp; Belum Ada Tagihan)
                                            </SelectItem>
                                            <SelectItem value="submitted">Sudah Unggah Lengkap (Submitted)</SelectItem>
                                            <SelectItem value="draft">Masih Mengisi (Draft)</SelectItem>
                                            <SelectItem value="not_started">Belum Buka Form Registrasi</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* 6. Status Tagihan & Bayar */}
                                <div className="w-full space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                                        <Wallet className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                        <span>Tagihan &amp; Bayar</span>
                                    </label>
                                    <Select
                                        value={filters.billing_status ?? "all"}
                                        onValueChange={(v) => setFilters((f) => ({ ...f, billing_status: v === "all" ? undefined : (v as any), page: 1 }))}
                                    >
                                        <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                            <SelectValue placeholder="Semua Tagihan &amp; Bayar" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl shadow-xl max-h-80">
                                            <SelectItem value="all">Semua Tagihan &amp; Bayar</SelectItem>
                                            <SelectGroup>
                                                <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">⚡ Prioritas / Tindakan Admin</SelectLabel>
                                                <SelectItem value="ready_to_bill">
                                                    <span className="font-semibold text-purple-700 dark:text-purple-300">Siap Buat Tagihan (Berkas Lengkap)</span>
                                                </SelectItem>
                                                <SelectItem value="submitted_unpaid">
                                                    <span className="font-medium text-amber-700 dark:text-amber-300">Berkas Lengkap Belum Lunas</span>
                                                </SelectItem>
                                            </SelectGroup>
                                            <SelectGroup>
                                                <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status Pembayaran</SelectLabel>
                                                <SelectItem value="paid">Sudah Dibayar (Lunas)</SelectItem>
                                                <SelectItem value="unpaid">Belum Dibayar (Unpaid)</SelectItem>
                                            </SelectGroup>
                                            <SelectGroup>
                                                <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Keberadaan Tagihan</SelectLabel>
                                                <SelectItem value="has_billing">Sudah Ada Tagihan (Semua)</SelectItem>
                                                <SelectItem value="no_billing">Belum Ada Tagihan</SelectItem>
                                            </SelectGroup>
                                            <SelectGroup>
                                                <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status Lainnya</SelectLabel>
                                                <SelectItem value="expired">Tagihan Kadaluarsa (Expired)</SelectItem>
                                                <SelectItem value="cancelled">Tagihan Dibatalkan (Cancelled)</SelectItem>
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Baris 3: Filter Chips / Tag Aktif (Bila Ada Filter Terpilih) */}
                            {(!!filters.conference_id || !!filters.output_route || !!filters.author_decision || !!filters.email_status || !!filters.registration_status || !!filters.billing_status || !!search) && (
                                <div className="flex items-center gap-2 flex-wrap pt-2.5 border-t border-slate-200/60 dark:border-slate-800 text-xs">
                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">Filter Aktif:</span>
                                    {filters.conference_id && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800 font-medium text-xs shadow-2xs">
                                            <GraduationCap className="w-3 h-3 text-blue-500" />
                                            Track: {conferences?.find((c: any) => String(c.conference_id) === String(filters.conference_id))?.name || filters.conference_id}
                                            <button onClick={() => setFilters((f) => ({ ...f, conference_id: undefined, page: 1 }))} className="hover:text-blue-900 ml-0.5 p-0.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    )}
                                    {filters.output_route && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800 font-medium text-xs shadow-2xs">
                                            <BookOpen className="w-3 h-3 text-purple-500" />
                                            Jalur: {filters.output_route === "journal" ? "Jurnal" : "Proceeding"}
                                            <button onClick={() => setFilters((f) => ({ ...f, output_route: undefined, page: 1 }))} className="hover:text-purple-900 ml-0.5 p-0.5 rounded-full hover:bg-purple-100 dark:hover:bg-purple-800 transition-colors">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    )}
                                    {filters.author_decision && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800 font-medium text-xs shadow-2xs">
                                            <Users className="w-3 h-3 text-amber-500" />
                                            Keputusan: {filters.author_decision.toUpperCase()}
                                            <button onClick={() => setFilters((f) => ({ ...f, author_decision: undefined, page: 1 }))} className="hover:text-amber-900 ml-0.5 p-0.5 rounded-full hover:bg-amber-100 dark:hover:bg-amber-800 transition-colors">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    )}
                                    {filters.email_status && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 font-medium text-xs shadow-2xs">
                                            <Mail className="w-3 h-3 text-emerald-500" />
                                            Email: {filters.email_status === "sent" ? "Terkirim" : "Belum Terkirim"}
                                            <button onClick={() => setFilters((f) => ({ ...f, email_status: undefined, page: 1 }))} className="hover:text-emerald-900 ml-0.5 p-0.5 rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-800 transition-colors">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    )}
                                    {filters.registration_status && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border border-teal-200 dark:border-teal-800 font-medium text-xs shadow-2xs">
                                            <FileText className="w-3 h-3 text-teal-500" />
                                            Kelengkapan: {REGISTRATION_STATUS_LABELS[filters.registration_status] || filters.registration_status}
                                            <button onClick={() => setFilters((f) => ({ ...f, registration_status: undefined, page: 1 }))} className="hover:text-teal-900 ml-0.5 p-0.5 rounded-full hover:bg-teal-100 dark:hover:bg-teal-800 transition-colors">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    )}
                                    {filters.billing_status && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 font-medium text-xs shadow-2xs">
                                            <Wallet className="w-3 h-3 text-indigo-500" />
                                            Tagihan: {BILLING_FILTER_LABELS[filters.billing_status] || filters.billing_status}
                                            <button onClick={() => setFilters((f) => ({ ...f, billing_status: undefined, page: 1 }))} className="hover:text-indigo-900 ml-0.5 p-0.5 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-800 transition-colors">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    )}
                                    {search && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700 font-medium text-xs shadow-2xs">
                                            <Search className="w-3 h-3 text-slate-400" />
                                            Pencarian: "{search}"
                                            <button onClick={() => setSearch("")} className="hover:text-slate-900 ml-0.5 p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-[950px]">
                                <thead>
                                    <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                        <th className="px-6 py-4 font-semibold">Paper</th>
                                        <th className="px-6 py-4 font-semibold">Track</th>
                                        <th className="px-6 py-4 font-semibold text-center">Jalur</th>
                                        <th className="px-6 py-4 font-semibold text-center">Keputusan</th>
                                        <th className="px-6 py-4 font-semibold text-center">Email</th>
                                        <th className="px-6 py-4 font-semibold text-center">Deadline</th>
                                        <th className="px-6 py-4 font-semibold text-right w-32 sticky right-0 bg-slate-50/80 dark:bg-slate-800/50 backdrop-blur-sm z-10 border-l border-slate-200 dark:border-slate-800 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                    {isLoading ? (
                                        <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />Memuat data...</td></tr>
                                    ) : filteredDecisions.length === 0 ? (
                                        <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-500">Data tidak ditemukan.</td></tr>
                                    ) : (
                                        filteredDecisions.map((d: any) => {
                                            const isExpanded = expandedRows.has(d.id);
                                            const journalName = findJournalName(d);
                                            return (
                                            <Fragment key={d.id}>
                                            <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors align-top">
                                                <td className="px-6 py-4 max-w-xs">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleExpandRow(d.id)}
                                                        className="flex items-start gap-1.5 text-left w-full group/expand"
                                                    >
                                                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-1" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-1" />}
                                                        <div className="min-w-0">
                                                            <div className={cn("font-bold text-slate-900 dark:text-white text-sm group-hover/expand:text-blue-600 dark:group-hover/expand:text-blue-400", !isExpanded && "line-clamp-2")}>
                                                                {d.registration?.submission_title || "-"}
                                                            </div>
                                                            <div className="text-xs text-slate-500 mt-0.5">{d.registration?.email}</div>
                                                            {isExpanded && (d.registration?.institution_name || d.registration?.participant_type || d.registration?.nim || d.registration?.nik) && (
                                                                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 flex items-center gap-1.5 flex-wrap">
                                                                    {d.registration?.institution_name && (
                                                                        <span className="font-medium text-slate-700 dark:text-slate-300">
                                                                            🏢 {d.registration.institution_name}
                                                                        </span>
                                                                    )}
                                                                    {d.registration?.nim && (
                                                                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-mono border border-slate-200 dark:border-slate-700">
                                                                            NIM: {d.registration.nim}
                                                                        </span>
                                                                    )}
                                                                    {d.registration?.nik && (
                                                                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-mono border border-slate-200 dark:border-slate-700">
                                                                            NIK: {d.registration.nik}
                                                                        </span>
                                                                    )}
                                                                    {d.registration?.participant_type && (
                                                                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold border border-slate-200 dark:border-slate-700">
                                                                            {d.registration.participant_type === "general"
                                                                                ? "Umum"
                                                                                : d.registration.participant_type.includes("s1")
                                                                                ? "Mhs S1/S2"
                                                                                : "Mhs S3"}
                                                                        </span>
                                                                    )}
                                                                    {d.registration?.is_presenter !== undefined && (
                                                                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">
                                                                            {d.registration.is_presenter ? "Presenter" : "Non-Presenter"}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {isExpanded && journalName && (
                                                                <div className="text-xs text-blue-600 dark:text-blue-400 mt-1.5 flex items-start gap-1">
                                                                    <BookOpen className="w-3 h-3 shrink-0 mt-0.5" />
                                                                    <span>{journalName}</span>
                                                                </div>
                                                            )}
                                                            {isExpanded && d.journal_admin_note && (
                                                                <div className="text-xs text-slate-500 mt-1 flex items-start gap-1 italic">
                                                                    <StickyNote className="w-3 h-3 shrink-0 mt-0.5" />
                                                                    <span>{d.journal_admin_note}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{d.conference?.name || "-"}</td>
                                                <td className="px-6 py-4 text-center">{routeBadge(d.output_route, d.publisher)}</td>
                                                <td className="px-6 py-4 text-center">{decisionBadge(d.author_decision)}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className={cn("inline-flex items-center justify-center w-7 h-7 rounded-full", d.decision_email_sent_at ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500")}>
                                                                    {d.decision_email_sent_at ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                                                                </span>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>{d.decision_email_sent_at ? `Terkirim ${formatDate(d.decision_email_sent_at)}` : "Belum pernah dikirim"}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </td>
                                                <td className="px-6 py-4 text-center text-xs text-slate-500">
                                                    {formatDate(d.output_route === "journal" ? d.decision_deadline : d.proceeding_submission_deadline)}
                                                </td>
                                                <td className="px-6 py-4 text-right sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 border-l border-slate-100 dark:border-slate-800/60 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <TooltipProvider delayDuration={200}>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" onClick={() => setDetailId(d.id)} className="h-8 w-8 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400">
                                                                        <Eye className="w-4 h-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent><p>Lihat detail</p></TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                        {d.output_route === "journal" && (
                                                            <TooltipProvider delayDuration={200}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button variant="ghost" size="icon" onClick={() => openEdit(d)} className="h-8 w-8 bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300">
                                                                            <Edit2 className="w-4 h-4" />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent><p>Edit jurnal & deadline</p></TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        )}
                                                        <DropdownMenu>
                                                            <TooltipProvider delayDuration={200}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <DropdownMenuTrigger asChild>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                disabled={sendEmail.isPending}
                                                                                className="h-8 w-8 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg relative"
                                                                            >
                                                                                {sendEmail.isPending && (sendEmail.variables as any)?.id === d.id ? (
                                                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                                                ) : (
                                                                                    <Mail className="w-4 h-4" />
                                                                                )}
                                                                            </Button>
                                                                        </DropdownMenuTrigger>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent><p>Pilihan kirim email</p></TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                            <DropdownMenuContent align="end" className="w-64 p-1.5 rounded-2xl shadow-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 z-50">
                                                                <div className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-800">
                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pilihan Format Email</p>
                                                                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate mt-0.5">{d.registration?.email}</p>
                                                                </div>
                                                                <DropdownMenuItem
                                                                    onClick={() => handleSendEmail(d.id, "regular")}
                                                                    disabled={sendEmail.isPending}
                                                                    className="rounded-xl py-2 px-2.5 text-xs cursor-pointer flex items-start gap-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 font-medium mt-1"
                                                                >
                                                                    <Send className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
                                                                    <div className="flex flex-col">
                                                                        <span className="font-semibold text-slate-800 dark:text-slate-200">Email Undangan Standar</span>
                                                                        <span className="text-[10px] text-slate-400 leading-tight">Format resmi Invitation to Publish awal</span>
                                                                    </div>
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem
                                                                    onClick={() => handleSendEmail(d.id, "reminder")}
                                                                    disabled={sendEmail.isPending}
                                                                    className="rounded-xl py-2 px-2.5 text-xs cursor-pointer flex items-start gap-2.5 hover:bg-amber-50 dark:hover:bg-amber-950/30 text-amber-900 dark:text-amber-200 font-medium"
                                                                >
                                                                    <Clock className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                                                                    <div className="flex flex-col">
                                                                        <span className="font-semibold text-amber-800 dark:text-amber-300">Email Reminder &amp; Perpanjangan</span>
                                                                        <span className="text-[10px] text-slate-400 leading-tight">Format pengingat perpanjangan naskah &amp; bayar</span>
                                                                    </div>
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                        {d.output_route === "proceeding" && (
                                                            <TooltipProvider delayDuration={200}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button variant="ghost" size="icon" onClick={() => { setExtendTarget(d); setExtendDate(toDateInputValue(d.proceeding_submission_deadline)); }} className="h-8 w-8 bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400">
                                                                            <Calendar className="w-4 h-4" />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent><p>Perpanjang deadline submit</p></TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && d.output_route === "proceeding" && (
                                                <tr className="bg-slate-50/60 dark:bg-slate-800/20">
                                                    <td colSpan={7} className="px-6 py-4">
                                                        <div className="pl-5 border-l-2 border-purple-200 dark:border-purple-800/50">
                                                            <p className="text-[11px] font-bold uppercase tracking-widest text-purple-500 mb-2 flex items-center gap-1.5">
                                                                <ListChecksIcon className="w-3.5 h-3.5" /> Kelengkapan &amp; Tagihan Proceeding
                                                            </p>
                                                            {completenessCell(d)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            </Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {data && data.total > 0 && (
                            <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                                <span className="text-slate-500 font-medium">Total {data.total} data</span>
                                <div className="flex items-center gap-4 sm:gap-6">
                                    <div className="flex items-center gap-2">
                                        <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                        <select
                                            value={filters.per_page ?? 20}
                                            onChange={(e) => setFilters((f) => ({ ...f, per_page: Number(e.target.value), page: 1 }))}
                                            className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                        >
                                            <option value="10">10</option>
                                            <option value="20">20</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, (f.page || 1) - 1) }))} disabled={filters.page === 1} className="h-8 px-3 rounded-lg">
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <span className="text-slate-600 dark:text-slate-400 font-medium min-w-[3rem] text-center">{data.current_page} / {data.last_page}</span>
                                        <Button variant="outline" size="sm" onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) + 1 }))} disabled={filters.page === data.last_page} className="h-8 px-3 rounded-lg">
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            {/* Import Dialog */}
            <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
                <DialogContent className="sm:max-w-[560px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-0 overflow-hidden shadow-2xl">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <DialogTitle className="text-xl font-bold flex items-center gap-3 text-slate-800 dark:text-white">
                            <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"><Upload className="w-5 h-5" /></div>
                            Import Pemetaan Output Conference
                        </DialogTitle>
                        <DialogDescription>Unggah file Excel hasil pemetaan jurnal/proceeding untuk membuat keputusan output per paper.</DialogDescription>
                    </DialogHeader>

                    {importResult ? (
                        <div className="p-6 max-h-[400px] overflow-y-auto">
                            <p className="font-semibold text-emerald-700 dark:text-emerald-400 mb-3">{importResult.message}</p>
                            {importResult.warnings?.length > 0 && (
                                <ul className="list-disc pl-5 space-y-1 text-sm text-amber-700 dark:text-amber-500">
                                    {importResult.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                                </ul>
                            )}
                            <div className="mt-6 flex justify-end">
                                <Button onClick={() => setIsImportOpen(false)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">Tutup</Button>
                            </div>
                        </div>
                    ) : (
                        <Tabs defaultValue="journal" className="p-6">
                            <TabsList className="grid grid-cols-3 mb-4">
                                <TabsTrigger value="journal">Jurnal</TabsTrigger>
                                <TabsTrigger value="proceeding">Proceeding</TabsTrigger>
                                <TabsTrigger value="consolidated">Konsolidasi</TabsTrigger>
                            </TabsList>

                            {(["journal", "proceeding"] as const).map((tab) => (
                                <TabsContent key={tab} value={tab} className="space-y-4">
                                    <div>
                                        <Label className="mb-2 block">Track Konferensi</Label>
                                        <Select value={importConferenceId} onValueChange={setImportConferenceId}>
                                            <SelectTrigger className="w-full h-11">
                                                <SelectValue placeholder="Pilih track" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {conferences?.map((conf: any) => (
                                                    <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>{conf.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <ImportFileDropzone file={importFile} onChange={setImportFile} />
                                    <Button variant="link" size="sm" className="px-0" onClick={() => downloadOutputTemplate(tab === "journal" ? "journal-mapping" : "proceeding-mapping", `template-${tab}-mapping.xlsx`)}>
                                        <Download className="w-4 h-4 mr-1" /> Unduh Template Excel
                                    </Button>
                                    <div className="flex justify-end gap-3 pt-2">
                                        <Button variant="outline" onClick={() => setIsImportOpen(false)} className="rounded-xl">Batal</Button>
                                        <Button
                                            onClick={() => handleImportSubmit(tab)}
                                            disabled={importJournalMapping.isPending || importProceedingMapping.isPending || !importFile}
                                            className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
                                        >
                                            {(importJournalMapping.isPending || importProceedingMapping.isPending) ? "Memproses..." : "Mulai Import"}
                                        </Button>
                                    </div>
                                </TabsContent>
                            ))}

                            <TabsContent value="consolidated" className="space-y-4">
                                <p className="text-sm text-slate-500">Import 1 file konsolidasi berisi pemetaan semua track sekaligus (Laporan Pemetaan Publikasi Konferensi).</p>
                                <ImportFileDropzone file={importFile} onChange={setImportFile} />
                                <Button variant="link" size="sm" className="px-0" onClick={() => downloadOutputTemplate("consolidated-mapping", "template-consolidated-mapping.xlsx")}>
                                    <Download className="w-4 h-4 mr-1" /> Unduh Template Excel
                                </Button>
                                <div className="flex justify-end gap-3 pt-2">
                                    <Button variant="outline" onClick={() => setIsImportOpen(false)} className="rounded-xl">Batal</Button>
                                    <Button onClick={() => handleImportSubmit("consolidated")} disabled={importConsolidatedMapping.isPending || !importFile} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                                        {importConsolidatedMapping.isPending ? "Memproses..." : "Mulai Import"}
                                    </Button>
                                </div>
                            </TabsContent>
                        </Tabs>
                    )}
                </DialogContent>
            </Dialog>

            {/* Riwayat Import Dialog */}
            <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
                <DialogContent className="sm:max-w-[640px] bg-white dark:bg-slate-900 rounded-3xl p-0 overflow-hidden">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <DialogTitle className="flex items-center gap-3 text-slate-800 dark:text-white">
                            <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"><History className="w-5 h-5" /></div>
                            Riwayat Import
                        </DialogTitle>
                        <DialogDescription>Histori file Excel yang pernah diimpor beserta hasil dan errornya.</DialogDescription>
                    </DialogHeader>

                    <div className="p-6 max-h-[60vh] overflow-y-auto space-y-3">
                        {isLoadingHistory ? (
                            <div className="flex justify-center py-8"><Loader2 className="w-7 h-7 animate-spin text-blue-500" /></div>
                        ) : !historyData || historyData.data.length === 0 ? (
                            <p className="text-center text-sm text-slate-500 py-8">Belum ada riwayat import.</p>
                        ) : (
                            historyData.data.map((batch) => {
                                const isOpen = expandedBatchId === batch.id;
                                const hasErrors = (batch.error_log?.length ?? 0) > 0;
                                return (
                                    <div key={batch.id} className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setExpandedBatchId(isOpen ? null : batch.id)}
                                            className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                                        >
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400">
                                                        {IMPORT_TYPE_LABELS[batch.import_type] || batch.import_type}
                                                    </span>
                                                    {batch.conference?.name && <span className="text-xs text-slate-500">{batch.conference.name}</span>}
                                                </div>
                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate mt-1">{batch.file_name}</p>
                                                <p className="text-xs text-slate-400 mt-0.5">{formatDate(batch.created_at)}</p>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <div className="text-right">
                                                    <p className="text-xs font-bold text-emerald-600">{batch.success_rows}/{batch.total_rows} berhasil</p>
                                                    {batch.failed_rows > 0 && <p className="text-xs font-bold text-red-500">{batch.failed_rows} gagal</p>}
                                                </div>
                                                {hasErrors && (isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />)}
                                            </div>
                                        </button>
                                        {isOpen && hasErrors && (
                                            <div className="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
                                                <ul className="space-y-1.5 mt-2">
                                                    {batch.error_log!.map((err, i) => (
                                                        <li key={i} className="text-xs text-amber-700 dark:text-amber-500 flex items-start gap-1.5">
                                                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                                            <span>{err.sheet ? `[${err.sheet}] ` : ""}{err.row ? `Baris ${err.row}: ` : ""}{err.reason}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {historyData && historyData.last_page > 1 && (
                        <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex items-center justify-between gap-4 text-sm">
                            <span className="text-slate-500 font-medium text-xs">Total {historyData.total} batch</span>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => setHistoryPage((p) => Math.max(1, p - 1))} disabled={historyPage === 1} className="h-8 px-3 rounded-lg"><ChevronLeft className="w-4 h-4" /></Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium min-w-[3rem] text-center">{historyData.current_page} / {historyData.last_page}</span>
                                <Button variant="outline" size="sm" onClick={() => setHistoryPage((p) => p + 1)} disabled={historyPage === historyData.last_page} className="h-8 px-3 rounded-lg"><ChevronRight className="w-4 h-4" /></Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Bulk Email Dialog */}
            <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
                <DialogContent className="w-[95vw] sm:max-w-[500px] p-0 overflow-hidden bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl">
                    <DialogHeader className="p-6 pb-4 bg-gradient-to-b from-blue-500/10 via-blue-500/5 to-transparent border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-3.5">
                            <div className="p-3 rounded-2xl bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20 shadow-sm shrink-0">
                                <Send className="w-6 h-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                                    Kirim Email Massal
                                </DialogTitle>
                                <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                                    Kirim notifikasi email sesuai filter track dan jalur publikasi yang sedang aktif (maksimal 50 paper per batch).
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="p-6 space-y-4">
                        {bulkResult ? (
                            <div className="space-y-4">
                                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800">
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{bulkResult.message}</p>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-center">
                                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 rounded-2xl p-3.5">
                                        <p className="text-2xl font-black text-emerald-600">{bulkResult.sent}</p>
                                        <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mt-0.5">Terkirim</p>
                                    </div>
                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 rounded-2xl p-3.5">
                                        <p className="text-2xl font-black text-red-600">{bulkResult.failed}</p>
                                        <p className="text-xs font-semibold text-red-700 dark:text-red-400 mt-0.5">Gagal</p>
                                    </div>
                                    <div className="bg-slate-100/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded-2xl p-3.5">
                                        <p className="text-2xl font-black text-slate-700 dark:text-slate-300">{bulkResult.remaining}</p>
                                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-0.5">Sisa Antrean</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block">
                                        Pilihan Template Email
                                    </Label>
                                    <Select value={bulkEmailTemplateType} onValueChange={(v: any) => setBulkEmailTemplateType(v)}>
                                        <SelectTrigger className="h-11 w-full min-w-0 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 font-medium text-sm">
                                            <span className="truncate text-left font-medium text-slate-800 dark:text-slate-200">
                                                {bulkEmailTemplateType === "reminder"
                                                    ? "[Reminder & Deadline Extension] Pengingat Perpanjangan"
                                                    : "Undangan Standar (Invitation to Publish)"}
                                            </span>
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl shadow-xl max-w-[calc(100vw-2rem)] sm:max-w-[480px]">
                                            <SelectItem value="regular" className="py-2.5">
                                                <div className="flex flex-col text-left">
                                                    <span className="font-semibold text-slate-800 dark:text-slate-200">Undangan Standar (Invitation to Publish)</span>
                                                    <span className="text-[11px] text-slate-400 mt-0.5 whitespace-normal">Format standar awal untuk keputusan seleksi</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="reminder" className="py-2.5">
                                                <div className="flex flex-col text-left">
                                                    <span className="font-semibold text-slate-800 dark:text-slate-200">[Reminder &amp; Deadline Extension] Pengingat Perpanjangan</span>
                                                    <span className="text-[11px] text-slate-400 mt-0.5 whitespace-normal">Format pengingat perpanjangan naskah &amp; pembayaran</span>
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <label className="flex items-start gap-3 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={bulkForce}
                                        onChange={(e) => setBulkForce(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                    />
                                    <div className="space-y-0.5 flex-1">
                                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Kirim ulang (Force resend)</span>
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                            Tetap kirimkan email ke naskah yang sebelumnya sudah berstatus "sent".
                                        </p>
                                    </div>
                                </label>

                                <div className="p-3 rounded-xl bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                    Filter aktif: <strong>{conferences?.find((c: any) => String(c.conference_id) === String(filters.conference_id))?.name || "Semua Track"}</strong> • Jalur: <strong>{filters.output_route ? (filters.output_route === "journal" ? "Jurnal" : "Proceeding") : "Semua"}</strong> • Keputusan: <strong>{filters.author_decision ? filters.author_decision.toUpperCase() : "Semua"}</strong> • Kelengkapan: <strong>{filters.registration_status ? (REGISTRATION_STATUS_LABELS[filters.registration_status] || filters.registration_status) : "Semua"}</strong> • Status Tagihan: <strong>{filters.billing_status ? (BILLING_FILTER_LABELS[filters.billing_status] || filters.billing_status) : "Semua"}</strong>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="p-4 px-6 bg-slate-50/70 dark:bg-slate-900/70 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2.5 sm:justify-end flex-wrap sm:flex-nowrap">
                        {bulkResult ? (
                            <>
                                <Button variant="outline" onClick={() => setIsBulkOpen(false)} className="rounded-xl h-10 px-5">Tutup</Button>
                                {bulkResult.remaining > 0 && (
                                    <Button
                                        onClick={() => handleBulkSend(filters.output_route)}
                                        disabled={sendBulkEmail.isPending}
                                        className="rounded-xl h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md shadow-blue-600/20"
                                    >
                                        Lanjut Kirim Batch Berikutnya
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={() => setIsBulkOpen(false)} className="rounded-xl h-10 px-5 text-slate-600 dark:text-slate-300">Batal</Button>
                                <Button
                                    onClick={() => handleBulkSend(filters.output_route)}
                                    disabled={sendBulkEmail.isPending}
                                    className="rounded-xl h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md shadow-blue-600/20"
                                >
                                    {sendBulkEmail.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                    Mulai Kirim
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Export Dialog */}
            <Dialog open={isExportOpen} onOpenChange={setIsExportOpen}>
                <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl">
                    <DialogHeader className="p-6 pb-4 bg-gradient-to-b from-blue-500/10 via-blue-500/5 to-transparent border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-3.5">
                            <div className="p-3 rounded-2xl bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20 shadow-sm shrink-0">
                                <Download className="w-6 h-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                                    Export Data Keputusan
                                </DialogTitle>
                                <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                                    Unduh rekapan berkas Excel sesuai kriteria filter dan pencarian yang aktif di layar saat ini.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="p-6 space-y-3">
                        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 p-4 space-y-2 text-xs text-slate-700 dark:text-slate-300">
                            <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-slate-700/60">
                                <span className="text-slate-500">Track Konferensi:</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{conferences?.find((c: any) => String(c.conference_id) === String(filters.conference_id))?.name || "Semua Track"}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-slate-700/60">
                                <span className="text-slate-500">Jalur Publikasi:</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{filters.output_route === "journal" ? "Jurnal" : filters.output_route === "proceeding" ? "Proceeding" : "Semua Jalur"}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-slate-700/60">
                                <span className="text-slate-500">Status Keputusan:</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{filters.author_decision ? filters.author_decision.toUpperCase() : "Semua Status"}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-slate-700/60">
                                <span className="text-slate-500">Status Email:</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{filters.email_status === "sent" ? "Terkirim" : filters.email_status === "unsent" ? "Belum Terkirim" : "Semua"}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-slate-700/60">
                                <span className="text-slate-500">Kelengkapan Prosiding:</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{filters.registration_status ? REGISTRATION_STATUS_LABELS[filters.registration_status] || filters.registration_status : "Semua"}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-slate-700/60">
                                <span className="text-slate-500">Status Tagihan / Bayar:</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{filters.billing_status ? BILLING_FILTER_LABELS[filters.billing_status] || filters.billing_status : "Semua"}</span>
                            </div>
                            {search && (
                                <div className="flex justify-between py-1">
                                    <span className="text-slate-500">Kata Kunci:</span>
                                    <span className="font-semibold text-blue-600 truncate max-w-[200px]">"{search}"</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="p-4 px-6 bg-slate-50/70 dark:bg-slate-900/70 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2.5 sm:justify-end">
                        <Button variant="outline" onClick={() => setIsExportOpen(false)} className="rounded-xl h-10 px-5 text-slate-600 dark:text-slate-300">Batal</Button>
                        <Button
                            onClick={handleExport}
                            disabled={isExporting}
                            className="rounded-xl h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md shadow-blue-600/20"
                        >
                            {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                            Unduh Excel Sekarang
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Extend Deadline Dialog */}
            <Dialog open={!!extendTarget} onOpenChange={() => setExtendTarget(null)}>
                <DialogContent className="sm:max-w-[440px] bg-white dark:bg-slate-900 rounded-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-600" /> Perpanjang Deadline Submit</DialogTitle>
                        <DialogDescription>{extendTarget?.registration?.submission_title}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="mb-2 block">Deadline Baru</Label>
                            <Input type="date" value={extendDate} onChange={(e) => setExtendDate(e.target.value)} />
                        </div>
                        <div>
                            <Label className="mb-2 block">Alasan (opsional)</Label>
                            <Input value={extendReason} onChange={(e) => setExtendReason(e.target.value)} placeholder="Contoh: permintaan khusus penulis" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExtendTarget(null)} className="rounded-xl">Batal</Button>
                        <Button onClick={handleExtend} disabled={extendDeadline.isPending || !extendDate} className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white">
                            {extendDeadline.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Extend Deadline Dialog */}
            <Dialog open={isBulkExtendOpen} onOpenChange={setIsBulkExtendOpen}>
                <DialogContent className="w-[95vw] sm:max-w-[560px] md:max-w-[580px] p-0 overflow-hidden bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl">
                    <DialogHeader className="p-6 pb-4 bg-gradient-to-b from-amber-500/12 via-amber-500/5 to-transparent border-b border-slate-100 dark:border-slate-800/80">
                        <div className="flex items-center gap-3.5">
                            <div className="p-3 rounded-2xl bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20 shadow-sm shrink-0">
                                <CalendarClock className="w-6 h-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                                    Perpanjang Deadline Massal
                                </DialogTitle>
                                <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                                    Perbarui batas waktu keputusan (Jurnal) atau registrasi (Proceeding) secara massal sekaligus kirim notifikasi.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="p-6 space-y-4 max-h-[72vh] overflow-y-auto">
                        {/* Kriteria Filter Target */}
                        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                    <Filter className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> Kriteria Target Paper
                                </span>
                                <span className="text-[11px] text-slate-400">Lingkup pembaruan</span>
                            </div>

                            <div>
                                <Label className="text-[11px] font-semibold text-slate-500 mb-1 block">Target Track Konferensi</Label>
                                <Select value={bulkExtendConferenceId} onValueChange={setBulkExtendConferenceId}>
                                    <SelectTrigger className="h-10 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-sm">
                                        <SelectValue placeholder="Semua Track" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl shadow-xl max-h-60">
                                        <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                        {conferences?.map((c: any) => (
                                             <SelectItem key={c.conference_id} value={String(c.conference_id)}>
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[11px] font-semibold text-slate-500 mb-1 block">Jalur Publikasi</Label>
                                    <Select value={bulkExtendRoute} onValueChange={(v: any) => setBulkExtendRoute(v)}>
                                        <SelectTrigger className="h-10 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl shadow-xl">
                                            <SelectItem value="all">Semua Jalur</SelectItem>
                                            <SelectItem value="journal">Hanya Jurnal</SelectItem>
                                            <SelectItem value="proceeding">Hanya Proceeding</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-[11px] font-semibold text-slate-500 mb-1 block">Status Keputusan</Label>
                                    <Select value={bulkExtendAuthorDecision} onValueChange={(v: any) => setBulkExtendAuthorDecision(v)}>
                                        <SelectTrigger className="h-10 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-sm">
                                            <SelectValue placeholder="Semua Keputusan" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl shadow-xl">
                                            <SelectItem value="all">Semua Keputusan</SelectItem>
                                            <SelectItem value="pending">Pending</SelectItem>
                                            <SelectItem value="agreed">Agreed</SelectItem>
                                            <SelectItem value="declined">Declined</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div>
                                <Label className="text-[11px] font-semibold text-slate-500 mb-1 block">Status Pembayaran / Tagihan</Label>
                                <Select value={bulkExtendBillingStatus} onValueChange={setBulkExtendBillingStatus}>
                                    <SelectTrigger className="h-10 w-full min-w-0 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-sm">
                                        <span className="truncate text-left font-normal text-slate-800 dark:text-slate-200">
                                            {bulkExtendBillingStatus === "all"
                                                ? "Semua Status Pembayaran / Tagihan"
                                                : (BILLING_FILTER_LABELS[bulkExtendBillingStatus] || bulkExtendBillingStatus)}
                                        </span>
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl shadow-xl max-h-64">
                                        <SelectItem value="all">Semua Status Pembayaran / Tagihan</SelectItem>
                                        <SelectGroup>
                                            <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status Pembayaran</SelectLabel>
                                            <SelectItem value="paid">Lunas / Sudah Bayar</SelectItem>
                                            <SelectItem value="unpaid">Belum Bayar (Menunggu Pembayaran)</SelectItem>
                                        </SelectGroup>
                                        <SelectGroup>
                                            <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Keberadaan Tagihan</SelectLabel>
                                            <SelectItem value="has_billing">Ada Tagihan (Sudah Registrasi Prosiding)</SelectItem>
                                            <SelectItem value="no_billing">Belum Ada Tagihan</SelectItem>
                                        </SelectGroup>
                                        <SelectGroup>
                                            <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status Lainnya</SelectLabel>
                                            <SelectItem value="expired">Tagihan Kadaluarsa</SelectItem>
                                            <SelectItem value="cancelled">Tagihan Dibatalkan</SelectItem>
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Ringkasan Estimasi Data Terdampak */}
                        <div className={cn(
                            "rounded-2xl border p-4 transition-all duration-200 flex items-center justify-between gap-3",
                            bulkExtendTotalAffected > 0
                                ? "bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border-amber-200/80 dark:border-amber-900/40"
                                : "bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-800"
                        )}>
                            <div className="flex items-center gap-3 min-w-0">
                                <div className={cn(
                                    "p-2.5 rounded-xl shrink-0",
                                    bulkExtendTotalAffected > 0
                                        ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                                        : "bg-slate-200/70 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                                )}>
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                        Total Paper Terdampak
                                    </p>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                                        {bulkExtendTotalAffected > 0
                                            ? "Paper yang memenuhi seluruh kriteria filter di atas"
                                            : "Tidak ada paper yang cocok dengan kombinasi filter ini"}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                {isLoadingBulkExtendCount || isFetchingBulkExtendCount ? (
                                    <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 py-1">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span className="text-xs font-semibold">Menghitung...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-baseline gap-1">
                                        <span className={cn(
                                            "text-2xl font-black",
                                            bulkExtendTotalAffected > 0
                                                ? "text-amber-600 dark:text-amber-400"
                                                : "text-slate-400 dark:text-slate-500"
                                        )}>
                                            {bulkExtendTotalAffected}
                                        </span>
                                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                                            paper
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Batas Waktu Baru (Deadline) */}
                        <div className="space-y-1.5">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> Batas Waktu Baru (Deadline)
                            </Label>
                            <Input
                                type="date"
                                value={bulkExtendDate}
                                onChange={(e) => setBulkExtendDate(e.target.value)}
                                className="h-11 rounded-xl font-semibold text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 shadow-sm text-sm"
                            />
                            <p className="text-[11px] text-slate-400">
                                Batas waktu ini otomatis diterapkan ke kolom deadline keputusan naskah dan tagihan registrasi terkait.
                            </p>
                        </div>

                        {/* Pengaturan Notifikasi Email & Template */}
                        <div className={cn(
                            "rounded-2xl border transition-all duration-200 p-4 space-y-3.5",
                            bulkExtendSendEmail
                                ? "bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40"
                                : "bg-slate-50/50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-800"
                        )}>
                            <label className="flex items-start gap-3 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={bulkExtendSendEmail}
                                    onChange={(e) => setBulkExtendSendEmail(e.target.checked)}
                                    className="mt-0.5 h-4.5 w-4.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                                />
                                <div className="space-y-0.5 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                                            Kirim Email Pengingat &amp; Notifikasi
                                        </span>
                                        {bulkExtendSendEmail && (
                                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-200/80 text-amber-900 dark:bg-amber-900/60 dark:text-amber-200">
                                                Aktif
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                        Otomatis mengirimkan email notifikasi dengan batas waktu baru kepada seluruh penulis yang terdampak.
                                    </p>
                                </div>
                            </label>

                            {bulkExtendSendEmail && (
                                <div className="pt-3 border-t border-amber-200/70 dark:border-amber-900/40 space-y-2.5">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-300 block">
                                            Pilihan Format Template Email
                                        </Label>
                                        <Select value={bulkExtendTemplateType} onValueChange={(v: any) => setBulkExtendTemplateType(v)}>
                                            <SelectTrigger className="h-10 w-full min-w-0 rounded-xl bg-white dark:bg-slate-900 border-amber-200 dark:border-amber-800 font-medium">
                                                <span className="truncate text-left font-medium text-slate-800 dark:text-slate-200">
                                                    {bulkExtendTemplateType === "reminder"
                                                        ? "[Reminder & Deadline Extension] - Format Pengingat & Perpanjangan"
                                                        : "[Invitation to Publish] - Format Undangan Standar Awal"}
                                                </span>
                                            </SelectTrigger>
                                            <SelectContent className="rounded-xl shadow-xl max-w-[calc(100vw-2rem)] sm:max-w-[520px]">
                                                <SelectItem value="reminder" className="py-2.5 cursor-pointer">
                                                    <div className="flex flex-col text-left">
                                                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                                                            [Reminder &amp; Deadline Extension] - Format Pengingat &amp; Perpanjangan
                                                        </span>
                                                        <span className="text-[11px] text-slate-400 mt-0.5 whitespace-normal">
                                                            Rekomendasi: Subjek reminder, perpanjangan waktu, &amp; klausul abaikan email
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="regular" className="py-2.5 cursor-pointer">
                                                    <div className="flex flex-col text-left">
                                                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                                                            [Invitation to Publish] - Format Undangan Standar Awal
                                                        </span>
                                                        <span className="text-[11px] text-slate-400 mt-0.5 whitespace-normal">
                                                            Format surat pengumuman dan undangan registrasi awal
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="p-3 rounded-xl bg-white/90 dark:bg-slate-900/90 border border-amber-200/70 dark:border-amber-900/50 text-xs space-y-1">
                                        <p className="font-semibold text-amber-900 dark:text-amber-300 flex items-center gap-1.5">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                                            {bulkExtendTemplateType === "reminder" ? "Detail Format Pengingat:" : "Detail Format Undangan:"}
                                        </p>
                                        <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                                            {bulkExtendTemplateType === "reminder"
                                                ? "Menggunakan subjek [Reminder & Deadline Extension] dan paragraf pengingat perpanjangan waktu naskah & pembayaran, serta klausul abaikan bila sudah menyelesaikan proses."
                                                : "Menggunakan subjek dan format surat undangan publikasi resmi awal (Invitation to Publish)."}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="p-4 px-6 bg-slate-50/70 dark:bg-slate-900/70 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2.5 sm:justify-end">
                        <Button variant="outline" onClick={() => setIsBulkExtendOpen(false)} className="rounded-xl h-10 px-5 text-slate-600 dark:text-slate-300">
                            Batal
                        </Button>
                        <Button
                            onClick={handleExecuteBulkExtend}
                            disabled={bulkExtend.isPending || !bulkExtendDate || isLoadingBulkExtendCount || bulkExtendTotalAffected === 0}
                            className="rounded-xl h-10 px-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-md shadow-amber-500/20"
                        >
                            {bulkExtend.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}
                            Proses Perpanjangan {bulkExtendTotalAffected > 0 ? `(${bulkExtendTotalAffected} Paper)` : ""}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Test Email Dialog (Khusus Uji Coba Pengembang/Admin) */}
            <Dialog open={isTestEmailOpen} onOpenChange={setIsTestEmailOpen}>
                <DialogContent className="w-[95vw] sm:max-w-[520px] p-0 overflow-hidden bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl">
                    <DialogHeader className="p-6 pb-4 bg-gradient-to-b from-indigo-500/12 via-indigo-500/5 to-transparent border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-3.5">
                            <div className="p-3 rounded-2xl bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 shadow-sm shrink-0">
                                <MailCheck className="w-6 h-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                                    Uji Coba Pengiriman Email
                                </DialogTitle>
                                <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                                    Kirim satu sampel email uji coba ke alamat Anda untuk memverifikasi tata letak, subjek, dan redaksi secara langsung di inbox.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="p-6 space-y-4 max-h-[72vh] overflow-y-auto">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5 block">Email Penerima Test</Label>
                            <Input
                                type="email"
                                value={testTargetEmail}
                                onChange={(e) => setTestTargetEmail(e.target.value)}
                                className="h-11 rounded-xl font-medium bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 shadow-sm text-sm"
                                placeholder="satria.widnyana@undiksha.ac.id"
                            />
                            <p className="text-[11px] text-slate-400 mt-1">
                                Bypass keamanan local aktif khusus untuk email ini agar email riil dapat terkirim dari sistem.
                            </p>
                        </div>

                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5 block">Format / Tipe Template</Label>
                            <Select value={testTemplateType} onValueChange={(v: any) => setTestTemplateType(v)}>
                                <SelectTrigger className="h-11 w-full min-w-0 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 font-medium">
                                    <span className="truncate text-left font-medium text-slate-800 dark:text-slate-200">
                                        {testTemplateType === "reminder"
                                            ? "[Reminder & Deadline Extension] - Pengingat & Perpanjangan"
                                            : "[Invitation to Publish] - Undangan Standar Awal"}
                                    </span>
                                </SelectTrigger>
                                <SelectContent className="rounded-xl shadow-xl max-w-[calc(100vw-2rem)] sm:max-w-[480px]">
                                    <SelectItem value="reminder" className="py-2.5">
                                        <div className="flex flex-col text-left">
                                            <span className="font-semibold text-slate-800 dark:text-slate-200">[Reminder &amp; Deadline Extension] - Pengingat &amp; Perpanjangan</span>
                                            <span className="text-[11px] text-slate-400 mt-0.5 whitespace-normal">Template pengingat perpanjangan batas waktu registrasi &amp; pembayaran</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="regular" className="py-2.5">
                                        <div className="flex flex-col text-left">
                                            <span className="font-semibold text-slate-800 dark:text-slate-200">[Invitation to Publish] - Undangan Standar Awal</span>
                                            <span className="text-[11px] text-slate-400 mt-0.5 whitespace-normal">Template surat undangan registrasi publikasi standar</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="p-4 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 text-xs text-indigo-900 dark:text-indigo-300 space-y-2">
                            <p className="font-bold flex items-center gap-1.5">
                                <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                                Informasi Redaksi yang Akan Dikirim:
                            </p>
                            {testTemplateType === "reminder" ? (
                                <ul className="space-y-1 text-[11px] text-indigo-800 dark:text-indigo-300 pl-5 list-disc leading-relaxed">
                                    <li><strong>Subject:</strong> [Reminder &amp; Deadline Extension] Publication in KnE Publishing Indexed Proceedings - (Nama Track)</li>
                                    <li><strong>Opening:</strong> Greetings from the Organizing Committee... We would like to kindly remind you...</li>
                                    <li><strong>Extension Note:</strong> To provide authors with additional time... Organizing Committee is pleased to announce an extension...</li>
                                    <li><strong>Closing:</strong> If you have already finalized your manuscript and completed the registration payment, please kindly disregard this reminder.</li>
                                </ul>
                            ) : (
                                <ul className="space-y-1 text-[11px] text-indigo-800 dark:text-indigo-300 pl-5 list-disc leading-relaxed">
                                    <li><strong>Subject:</strong> Invitation to Publish in KnE Publishing Indexed Proceedings - (Nama Track)</li>
                                    <li><strong>Opening:</strong> We are pleased to inform you that your paper... has been selected for publication...</li>
                                    <li><strong>Closing:</strong> We would like to congratulate you on your paper acceptance...</li>
                                </ul>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="p-4 px-6 bg-slate-50/70 dark:bg-slate-900/70 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2.5 sm:justify-end">
                        <Button variant="outline" onClick={() => setIsTestEmailOpen(false)} className="rounded-xl h-10 px-5 text-slate-600 dark:text-slate-300">
                            Batal
                        </Button>
                        <Button
                            onClick={handleSendTestEmail}
                            disabled={testSendEmail.isPending || !testTargetEmail}
                            className="rounded-xl h-10 px-6 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-semibold shadow-md shadow-indigo-600/20"
                        >
                            {testSendEmail.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                            Kirim Test Email Sekarang
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Edit Decision Dialog */}
            <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
                <DialogContent className="sm:max-w-[480px] bg-white dark:bg-slate-900 rounded-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Edit2 className="w-5 h-5 text-slate-600" /> Edit Keputusan</DialogTitle>
                        <DialogDescription>{editTarget?.registration?.submission_title}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="mb-2 block">Jurnal Scopus</Label>
                            <Popover modal open={openScopusCombobox} onOpenChange={setOpenScopusCombobox}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openScopusCombobox}
                                        className={cn("w-full h-11 justify-between rounded-xl font-normal", !editForm.scopus_journal_id && "text-muted-foreground")}
                                    >
                                        <span className="truncate">
                                            {editForm.scopus_journal_id
                                                ? scopusJournals?.find((j) => String(j.id) === editForm.scopus_journal_id)?.journal_name
                                                : "Tidak dipilih"}
                                        </span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl shadow-xl max-h-72 z-[100]" align="start">
                                    <Command>
                                        <CommandInput placeholder="Cari nama jurnal Scopus..." />
                                        <CommandList>
                                            <CommandEmpty>Jurnal tidak ditemukan.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandItem value="Tidak dipilih none" onSelect={() => { setEditForm((f) => ({ ...f, scopus_journal_id: "" })); setOpenScopusCombobox(false); }}>
                                                    <Check className={cn("mr-2 h-4 w-4", !editForm.scopus_journal_id ? "opacity-100" : "opacity-0")} />
                                                    <span className="text-slate-400 italic">-- Tidak dipilih --</span>
                                                </CommandItem>
                                                {scopusJournals?.map((j) => (
                                                    <CommandItem
                                                        key={j.id}
                                                        value={`${j.journal_name} ${j.quartile}`}
                                                        onSelect={() => { setEditForm((f) => ({ ...f, scopus_journal_id: String(j.id), sinta_journal_id: "" })); setOpenScopusCombobox(false); }}
                                                    >
                                                        <Check className={cn("mr-2 h-4 w-4", editForm.scopus_journal_id === String(j.id) ? "opacity-100" : "opacity-0")} />
                                                        <span className="truncate">{j.journal_name} ({j.quartile})</span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div>
                            <Label className="mb-2 block">Jurnal Sinta</Label>
                            <Popover modal open={openSintaCombobox} onOpenChange={setOpenSintaCombobox}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openSintaCombobox}
                                        className={cn("w-full h-11 justify-between rounded-xl font-normal", !editForm.sinta_journal_id && "text-muted-foreground")}
                                    >
                                        <span className="truncate">
                                            {editForm.sinta_journal_id
                                                ? sintaJournals?.find((j) => String(j.id) === editForm.sinta_journal_id)?.journal_name
                                                : "Tidak dipilih"}
                                        </span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl shadow-xl max-h-72 z-[100]" align="start">
                                    <Command>
                                        <CommandInput placeholder="Cari nama jurnal Sinta..." />
                                        <CommandList>
                                            <CommandEmpty>Jurnal tidak ditemukan.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandItem value="Tidak dipilih none" onSelect={() => { setEditForm((f) => ({ ...f, sinta_journal_id: "" })); setOpenSintaCombobox(false); }}>
                                                    <Check className={cn("mr-2 h-4 w-4", !editForm.sinta_journal_id ? "opacity-100" : "opacity-0")} />
                                                    <span className="text-slate-400 italic">-- Tidak dipilih --</span>
                                                </CommandItem>
                                                {sintaJournals?.map((j) => (
                                                    <CommandItem
                                                        key={j.id}
                                                        value={`${j.journal_name} ${j.sinta_rank}`}
                                                        onSelect={() => { setEditForm((f) => ({ ...f, sinta_journal_id: String(j.id), scopus_journal_id: "" })); setOpenSintaCombobox(false); }}
                                                    >
                                                        <Check className={cn("mr-2 h-4 w-4", editForm.sinta_journal_id === String(j.id) ? "opacity-100" : "opacity-0")} />
                                                        <span className="truncate">{j.journal_name} ({j.sinta_rank})</span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div>
                            <Label className="mb-2 block">Catatan Admin</Label>
                            <Textarea value={editForm.journal_admin_note} onChange={(e) => setEditForm((f) => ({ ...f, journal_admin_note: e.target.value }))} className="h-20" />
                        </div>
                        <div>
                            <Label className="mb-2 block">Deadline Keputusan</Label>
                            <Input type="date" value={editForm.decision_deadline} onChange={(e) => setEditForm((f) => ({ ...f, decision_deadline: e.target.value }))} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditTarget(null)} className="rounded-xl">Batal</Button>
                        <Button onClick={handleSaveEdit} disabled={updateDecision.isPending} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                            {updateDecision.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={!!detailId} onOpenChange={() => setDetailId(null)}>
                <DialogContent className="!max-w-[95vw] sm:!max-w-2xl lg:!max-w-3xl bg-white dark:bg-slate-900 rounded-3xl p-0 overflow-hidden">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-indigo-600" /> Detail Keputusan</DialogTitle>
                        <DialogDescription>Informasi lengkap paper, keputusan yang dipilih, dan kelengkapan registrasi/tagihan - supaya operator tahu persis posisi paper ini.</DialogDescription>
                    </DialogHeader>

                    {isLoadingDetail || !detailData ? (
                        <div className="p-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
                    ) : (
                        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-1.5"><FileText className="w-3.5 h-3.5" /> Judul Paper</span>
                                <p className="font-bold text-sm text-slate-900 dark:text-white leading-relaxed">{detailData.registration?.submission_title || "-"}</p>
                                {detailData.registration?.submission_authors && (
                                    <p className="text-xs text-slate-500 mt-1">Penulis: {detailData.registration.submission_authors}</p>
                                )}
                                <p className="text-xs text-slate-500 mt-0.5">{detailData.registration?.email}</p>
                                {(detailData.registration?.institution_name || detailData.registration?.participant_type || detailData.registration?.nim || detailData.registration?.nik) && (
                                    <div className="text-xs text-slate-600 dark:text-slate-400 mt-2 flex items-center gap-2 flex-wrap">
                                        {detailData.registration?.institution_name && (
                                            <span><strong>Institusi:</strong> {detailData.registration.institution_name}</span>
                                        )}
                                        {detailData.registration?.nim && (
                                            <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[11px] font-mono border border-slate-200 dark:border-slate-700">
                                                <strong>NIM:</strong> {detailData.registration.nim}
                                            </span>
                                        )}
                                        {detailData.registration?.nik && (
                                            <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[11px] font-mono border border-slate-200 dark:border-slate-700">
                                                <strong>NIK:</strong> {detailData.registration.nik}
                                            </span>
                                        )}
                                        {detailData.registration?.participant_type && (
                                            <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[11px] font-semibold border border-slate-200 dark:border-slate-700">
                                                {detailData.registration.participant_type === "general"
                                                    ? "Umum"
                                                    : detailData.registration.participant_type.includes("s1")
                                                    ? "Mahasiswa (S1/S2)"
                                                    : "Mahasiswa (S3)"}
                                            </span>
                                        )}
                                        {detailData.registration?.is_presenter !== undefined && (
                                            <span className="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[11px] font-semibold border border-blue-200 dark:border-blue-800">
                                                {detailData.registration.is_presenter ? "Presenter" : "Non-Presenter"}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Track</span>
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{detailData.conference?.name || "-"}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Jalur Output</span>
                                    <div className="mt-1">{routeBadge(detailData.output_route, detailData.publisher)}</div>
                                </div>
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Keputusan Penulis</span>
                                    <div className="mt-1">{decisionBadge(detailData.author_decision)}</div>
                                </div>
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Tanggal Keputusan</span>
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{formatDate(detailData.author_decision_at)}</p>
                                </div>
                            </div>

                            {detailData.output_route === "journal" && (
                                <div className="bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-2xl p-4">
                                    <span className="text-xs font-bold uppercase tracking-widest text-blue-500 flex items-center gap-1.5 mb-1.5"><BookOpen className="w-3.5 h-3.5" /> Jurnal Ditargetkan</span>
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                        {detailData.scopus_journal?.journal_name
                                            ? `${detailData.scopus_journal.journal_name} (${detailData.scopus_journal.quartile})`
                                            : detailData.sinta_journal?.journal_name
                                                ? `${detailData.sinta_journal.journal_name} (${detailData.sinta_journal.sinta_rank})`
                                                : "Belum dipetakan"}
                                    </p>
                                    {(detailData.scopus_journal?.journal_url || detailData.sinta_journal?.journal_url) && (
                                        <a href={detailData.scopus_journal?.journal_url || detailData.sinta_journal?.journal_url || undefined} target="_blank" rel="noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                                            {detailData.scopus_journal?.journal_url || detailData.sinta_journal?.journal_url}
                                        </a>
                                    )}
                                    <p className="text-xs text-slate-500 mt-1">Deadline keputusan: {formatDate(detailData.decision_deadline)}</p>
                                    {detailData.journal_admin_note && <p className="text-xs text-slate-500 mt-1 italic">Catatan: {detailData.journal_admin_note}</p>}
                                    <p className="text-xs text-slate-500 mt-1">Email dikirim: {detailData.decision_email_sent_at ? formatDate(detailData.decision_email_sent_at) : "Belum pernah"}</p>
                                    
                                    {detailData.decision_token && (
                                        <div className="mt-3 pt-3 border-t border-blue-100 dark:border-blue-900/30 space-y-1.5">
                                            <span className="text-xs font-bold uppercase tracking-widest text-blue-500 flex items-center gap-1.5 mb-1"><Send className="w-3.5 h-3.5" /> Link Keputusan (Dikirim via Email)</span>
                                            <a href={`${window.location.origin}/conference-output/decision/${detailData.decision_token}`} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline break-all block bg-white dark:bg-slate-900 p-2.5 rounded-xl border border-blue-100 dark:border-blue-800">
                                                {`${window.location.origin}/conference-output/decision/${detailData.decision_token}`}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}

                            {detailData.output_route === "proceeding" && (
                                <div className="bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 rounded-2xl p-4 space-y-1">
                                    <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5 mb-1.5"><BookOpen className="w-3.5 h-3.5" /> Proceeding ({detailData.publisher || "-"})</span>
                                    <p className="text-xs text-slate-500">Deadline submit registrasi: {formatDate(detailData.proceeding_submission_deadline)}</p>
                                    <p className="text-xs text-slate-500">Email registrasi dikirim: {detailData.decision_email_sent_at ? formatDate(detailData.decision_email_sent_at) : "Belum pernah"}</p>
                                    {detailData.output_registration && (
                                        <>
                                            <p className="text-xs text-slate-500">Status registrasi: <span className="font-semibold">{detailData.output_registration.status}</span></p>
                                            <div className="pt-2 mt-2 border-t border-purple-100 dark:border-purple-900/30 space-y-1">
                                                <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5 mb-1"><ListChecksIcon className="w-3.5 h-3.5" /> Kelengkapan</span>
                                                <FileChecklistLine label="Camera-ready" url={detailData.output_registration.camera_ready_file_path} />
                                                <FileChecklistLine label="Copyright form" url={detailData.output_registration.copyright_form_file_path} />
                                                <ChecklistLine label="Orisinalitas < 20% similarity" done={!!detailData.output_registration.checklist_originality_similarity_below_20} />
                                                <ChecklistLine label="Metadata sesuai ketentuan publisher" done={!!detailData.output_registration.checklist_metadata_matches_publisher} />
                                                <ChecklistLine label="Menggunakan template resmi" done={!!detailData.output_registration.checklist_used_template} />
                                            </div>
                                            {(detailData.output_registration.authors?.length ?? 0) > 0 && (
                                                <div className="pt-2 mt-2 border-t border-purple-100 dark:border-purple-900/30">
                                                    <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5 mb-1.5"><Users className="w-3.5 h-3.5" /> Daftar Penulis</span>
                                                    <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                                                        {detailData.output_registration.authors?.map((a: any) => (
                                                            <li key={a.id}>
                                                                {a.given_name} {a.family_name} {a.is_corresponding && <span className="text-purple-600 font-semibold">(Corresponding)</span>}
                                                                {a.affiliation && <span className="text-slate-400"> - {a.affiliation}</span>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            
                                            {detailData.output_registration.public_token && (
                                                <div className="pt-3 mt-3 border-t border-purple-100 dark:border-purple-900/30 space-y-1.5">
                                                    <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5 mb-1"><Send className="w-3.5 h-3.5" /> Link Registrasi (Dikirim via Email)</span>
                                                    <a href={`${window.location.origin}/conference-output/registration/${detailData.output_registration.public_token}`} target="_blank" rel="noreferrer" className="text-[11px] text-purple-600 dark:text-purple-400 hover:underline break-all block bg-white dark:bg-slate-900 p-2.5 rounded-xl border border-purple-100 dark:border-purple-800">
                                                        {`${window.location.origin}/conference-output/registration/${detailData.output_registration.public_token}`}
                                                    </a>
                                                </div>
                                            )}
                                            
                                            <div className="pt-3 mt-3 border-t border-purple-100 dark:border-purple-900/30">
                                                <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5 mb-1.5"><Wallet className="w-3.5 h-3.5" /> Status Tagihan</span>
                                                {detailData.output_registration.billing ? (
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        {billingBadge(detailData.output_registration.billing)}
                                                        <span className="text-xs text-slate-500">{detailData.output_registration.billing.invoice_number}</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 p-3 rounded-xl mt-2">
                                                        <div>
                                                            <span className="text-xs font-semibold text-amber-800 dark:text-amber-400 block">Belum ada tagihan dibuat</span>
                                                            <span className="text-[11px] text-amber-600 dark:text-amber-500">
                                                                {detailData.output_registration.status === "submitted"
                                                                    ? "Penulis sudah submit kelengkapan. Anda dapat menerbitkan tagihan pembayaran sekarang."
                                                                    : "Penulis masih mengisi form (draft)."}
                                                            </span>
                                                        </div>
                                                        {detailData.output_registration.status === "submitted" && (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setGenerateInvoiceConfirm({
                                                                    id: detailData.id,
                                                                    title: detailData.registration?.submission_title || detailData.output_registration?.paper_title_final || "-",
                                                                    author: detailData.registration?.email || "-",
                                                                    publisher: detailData.publisher || "KNE",
                                                                })}
                                                                disabled={generateInvoice.isPending}
                                                                className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold h-8 shadow-sm flex items-center gap-1.5 shrink-0"
                                                            >
                                                                {generateInvoice.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />} Terbitkan Tagihan
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {detailData.output_registration.billing && (
                                                <div className="pt-3 mt-3 border-t border-purple-100 dark:border-purple-900/30">
                                                    {detailData.output_registration.public_token && (
                                                        <div className="space-y-1.5">
                                                            <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5 mb-1"><Send className="w-3.5 h-3.5" /> Link Pembayaran (Dikirim via Email)</span>
                                                            <a href={`${window.location.origin}/conference-output/billing/${detailData.output_registration.public_token}`} target="_blank" rel="noreferrer" className="text-[11px] text-purple-600 dark:text-purple-400 hover:underline break-all block bg-white dark:bg-slate-900 p-2.5 rounded-xl border border-purple-100 dark:border-purple-800">
                                                                {`${window.location.origin}/conference-output/billing/${detailData.output_registration.public_token}`}
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {detailData.import_batch && (
                                <p className="text-[11px] text-slate-400">Diimpor dari batch: {detailData.import_batch.file_name} ({formatDate(detailData.created_at)})</p>
                            )}
                        </div>
                    )}

                    <DialogFooter className="p-6 pt-0 sm:justify-between">
                        {detailData && detailData.author_decision !== "pending" && (
                            <Button
                                variant="outline"
                                onClick={() => handleCancelDecision(detailData.id)}
                                disabled={cancelDecision.isPending}
                                className="rounded-xl border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                                {cancelDecision.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />} Batalkan Keputusan
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => setDetailId(null)} className="rounded-xl">Tutup</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Konfirmasi Batalkan Keputusan - cuma muncul kalau sudah ada file/tagihan yang akan terhapus */}
            <Dialog open={!!cancelConfirm} onOpenChange={() => setCancelConfirm(null)}>
                <DialogContent className="sm:max-w-[480px] bg-white dark:bg-slate-900 rounded-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> Konfirmasi Pembatalan</DialogTitle>
                        <DialogDescription>Paper ini sudah punya data registrasi proceeding. Melanjutkan akan menghapus data berikut secara permanen:</DialogDescription>
                    </DialogHeader>
                    <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-2 bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-xl p-4">
                        {cancelConfirm?.summary.has_camera_ready && <li className="flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-red-500 shrink-0" /> File camera-ready yang sudah diunggah penulis</li>}
                        {cancelConfirm?.summary.has_copyright_form && <li className="flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-red-500 shrink-0" /> File copyright form yang sudah diunggah penulis</li>}
                        {cancelConfirm?.summary.has_billing && (
                            <li className="flex items-center gap-2"><Wallet className="w-4 h-4 text-red-500 shrink-0" /> Tagihan {cancelConfirm.summary.billing_invoice_number} (status {cancelConfirm.summary.billing_status})</li>
                        )}
                    </ul>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCancelConfirm(null)} className="rounded-xl">Batal</Button>
                        <Button
                            onClick={() => cancelConfirm && handleCancelDecision(cancelConfirm.id, true)}
                            disabled={cancelDecision.isPending}
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
                        >
                            {cancelDecision.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Ya, Batalkan Permanen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Konfirmasi Generate Tagihan Pembayaran */}
            <Dialog open={!!generateInvoiceConfirm} onOpenChange={() => setGenerateInvoiceConfirm(null)}>
                <DialogContent className="sm:max-w-[480px] bg-white dark:bg-slate-900 rounded-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                            <CreditCard className="w-5 h-5" /> Terbitkan Tagihan Pembayaran
                        </DialogTitle>
                        <DialogDescription>
                            Sistem akan membuat nomor Virtual Account pembayaran di Payment Gateway untuk paper ini.
                        </DialogDescription>
                    </DialogHeader>
                    {generateInvoiceConfirm && (
                        <div className="space-y-3 text-xs bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 rounded-xl p-4 text-slate-700 dark:text-slate-300">
                            <div>
                                <span className="text-slate-400 block mb-0.5">Judul Paper:</span>
                                <span className="font-semibold block">{generateInvoiceConfirm.title}</span>
                            </div>
                            <div>
                                <span className="text-slate-400 block mb-0.5">Email Penulis:</span>
                                <span className="font-semibold block">{generateInvoiceConfirm.author}</span>
                            </div>
                            <div>
                                <span className="text-slate-400 block mb-0.5">Jalur Publikasi:</span>
                                <span className="font-semibold block">Prosiding ({generateInvoiceConfirm.publisher})</span>
                            </div>
                            <div className="pt-2 border-t border-purple-100 dark:border-purple-900/30 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="send_email_invoice"
                                    checked={sendEmailWithInvoice}
                                    onChange={(e) => setSendEmailWithInvoice(e.target.checked)}
                                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-slate-300 dark:border-slate-700 cursor-pointer"
                                />
                                <label htmlFor="send_email_invoice" className="font-medium cursor-pointer text-slate-700 dark:text-slate-300">
                                    Kirim email notifikasi invoice ke penulis secara otomatis
                                </label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setGenerateInvoiceConfirm(null)} className="rounded-xl">Batal</Button>
                        <Button
                            onClick={() => generateInvoiceConfirm && handleGenerateInvoice(generateInvoiceConfirm.id)}
                            disabled={generateInvoice.isPending}
                            className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white"
                        >
                            {generateInvoice.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />} Ya, Terbitkan Tagihan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: number | undefined; color: "blue" | "amber" | "emerald" | "purple" | "red" | "slate" }) {
    const colorMap: Record<string, string> = {
        blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
        amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
        emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
        purple: "bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
        red: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400",
        slate: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    };
    return (
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-4">
            <div className={`p-3 rounded-xl shrink-0 ${colorMap[color]}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <p className="text-2xl font-black text-slate-900 dark:text-white leading-tight">
                    {value === undefined ? <Loader2 className="w-5 h-5 animate-spin text-slate-300" /> : value}
                </p>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate">{label}</p>
            </div>
        </div>
    );
}

function ChecklistLine({ label, done }: { label: string; done: boolean }) {
    return (
        <div className="flex items-center gap-2 text-xs">
            {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 shrink-0" />}
            <span className={done ? "text-slate-600 dark:text-slate-300" : "text-slate-400 dark:text-slate-500"}>{label}</span>
        </div>
    );
}

function FileChecklistLine({ label, url }: { label: string; url?: string | null }) {
    if (!url) {
        return (
            <div className="flex items-center gap-2 text-xs">
                <XCircle className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 shrink-0" />
                <span className="text-slate-400 dark:text-slate-500">{label} - belum diunggah</span>
            </div>
        );
    }
    return (
        <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs hover:underline">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span className="text-slate-600 dark:text-slate-300">{label}</span>
            <Download className="w-3 h-3 text-slate-400 shrink-0" />
        </a>
    );
}

function ImportFileDropzone({ file, onChange }: { file: File | null; onChange: (f: File | null) => void }) {
    return (
        <div className="relative group">
            <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/10 border-2 border-dashed border-blue-200 dark:border-blue-800/50 rounded-2xl group-hover:border-blue-400 transition-colors pointer-events-none flex flex-col items-center justify-center p-6 gap-2">
                <FileUp className="w-6 h-6 text-blue-500" />
                <p className="font-semibold text-slate-700 dark:text-slate-200 text-sm">{file ? file.name : "Pilih file Excel"}</p>
                <p className="text-xs text-slate-500">.xlsx atau .xls</p>
            </div>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => onChange(e.target.files?.[0] || null)} className="w-full h-[110px] opacity-0 cursor-pointer" />
            {file && (
                <button type="button" onClick={() => onChange(null)} className="absolute top-2 right-2 text-red-500 bg-white dark:bg-slate-900 rounded-full p-1 shadow z-10">
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}
